import { CareHistory } from "@/features/care-queue/components/CareHistory";
import { MemberDeletion } from "@/features/members/components/MemberDeletion";
import { useEffectivePermissions } from "@/features/authorization/hooks/useEffectivePermissions";
import { MemberMinistryMemberships } from "@/features/memberships/MemberMinistryMemberships";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Brain,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  HeartHandshake,
  Home,
  Mail,
  Phone,
  ShieldCheck,
  Sparkles,
  UserRound,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type {
  HouseholdSummary,
  MemberAttendanceRecord,
  MemberAttendanceSummary,
  MemberIntelligenceResponse,
  MemberResponse,
} from "@/api/members";
import {
  getHouseholdSummary,
  getMember,
  getMemberAttendanceHistory,
  getMemberAttendanceSummary,
  getMemberAssessmentContext,
} from "@/api/members";
import type { CareQueueListResponse } from "@/api/careQueue";
import { getMemberCareQueue } from "@/api/careQueue";
import { Card } from "@/components/ui/card";
import { LoadingPage } from "@/components/ui/LoadingPage";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { PageContainer } from "@/design-system";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { MemberApplicationAccessCard } from "@/features/members/components/MemberApplicationAccessCard";
import { EditMemberDialog } from "@/features/members/components/EditMemberDialog";

export function MemberProfilePage() {
  const { memberId } = useParams();
  const { permissions } = useEffectivePermissions();
  const { churchId } = useAuth();

  const memberQuery = useQuery({
    queryKey: ["member", churchId, memberId],
    enabled: !!churchId && !!memberId,
    queryFn: () => getMember(churchId!, memberId!),
  });

  const intelligenceQuery = useQuery({
    queryKey: ["member-assessment-context", churchId, memberId],
    enabled: !!churchId && !!memberId && permissions.has("INTELLIGENCE_VIEW"),
    queryFn: () => getMemberAssessmentContext(churchId!, memberId!),
  });

  const attendanceQuery = useQuery({
    queryKey: ["member-attendance-history", churchId, memberId],
    enabled: !!churchId && !!memberId && permissions.has("ATTENDANCE_VIEW"),
    queryFn: () => getMemberAttendanceHistory(churchId!, memberId!),
  });

  const attendanceSummaryQuery = useQuery({
    queryKey: ["member-attendance-summary", churchId, memberId],
    enabled: !!churchId && !!memberId && permissions.has("ATTENDANCE_VIEW"),
    queryFn: () =>
      getMemberAttendanceSummary(churchId!, memberId!),
  });

  const careQueueQuery = useQuery({
    queryKey: ["member-care-queue", churchId, memberId],
    enabled: !!churchId && !!memberId && permissions.has("CARE_VIEW"),
    queryFn: () => getMemberCareQueue(churchId!, memberId!),
  });

  const householdQuery = useQuery({
    queryKey: ["household-summary", churchId, memberId],
    enabled: !!churchId && !!memberId && permissions.has("HOUSEHOLDS_VIEW"),
    queryFn: () => getHouseholdSummary(churchId!, memberId!),
  });

  if (memberQuery.isLoading) {
    return (
      <PageContainer>
        <LoadingPage />
      </PageContainer>
    );
  }

  if (memberQuery.isError || !memberQuery.data) {
    return (
      <PageContainer>
        <div className="rounded-2xl border border-rose-500/20 bg-rose-500/5 p-6 text-rose-300">
          Unable to load member.
        </div>
      </PageContainer>
    );
  }

  const member = memberQuery.data;
  const intelligence = intelligenceQuery.data?.assessment ?? undefined;
  const attendance = attendanceQuery.data ?? [];
  const attendanceSummary = attendanceSummaryQuery.data;
  const scopedAttendance = intelligenceQuery.data?.attendance;
  const scopedSummary: MemberAttendanceSummary | undefined = scopedAttendance && typeof scopedAttendance.eligible_occurrences === "number" && typeof scopedAttendance.attended_occurrences === "number" ? {
    eligible_services: scopedAttendance.eligible_occurrences,
    attended_services: scopedAttendance.attended_occurrences,
    missed_services: scopedAttendance.eligible_occurrences - scopedAttendance.attended_occurrences,
    attendance_rate: scopedAttendance.attendance_rate ?? null,
    consecutive_absences: scopedAttendance.consecutive_absences,
  } : undefined;
  const careQueue = careQueueQuery.data;
  const household = householdQuery.data;

  const fullName = [
    member.first_name,
    member.middle_name,
    member.last_name,
  ]
    .filter(Boolean)
    .join(" ");

  const displayName = member.preferred_name
    ? `${member.preferred_name} ${member.last_name}`
    : fullName;

  return (
    <PageContainer>
      {permissions.has("INTELLIGENCE_VIEW") && intelligenceQuery.data && (
        <p className="mb-4 rounded-xl border border-slate-800 p-4 text-slate-300">
          Intelligence context: {intelligenceQuery.data.context_name}.
          {intelligenceQuery.data.assessment ? " Risk and recommendations use this assessment only." : " Not yet assessed; no substitute score is shown."}
          Attendance history and summary include all eligible ministry contexts.
        </p>
      )}
      <div className="mb-5 flex items-center gap-2 text-sm text-slate-400">
        <Link to="/members" className="hover:text-white">
          Members
        </Link>

        <span>/</span>

        <span className="text-white">
          {displayName}
        </span>
      </div>

      <MemberHero
        member={member}
        household={household}
        displayName={displayName}
      />

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
        <div className="space-y-6">
          {["INTELLIGENCE_VIEW", "ATTENDANCE_VIEW", "CARE_VIEW", "HOUSEHOLDS_VIEW"].every(code => permissions.has(code)) && <IntelligenceOverview
            intelligence={intelligence}
            isLoading={
              intelligenceQuery.isLoading ||
              attendanceSummaryQuery.isLoading
            }
            isError={
              intelligenceQuery.isError ||
              attendanceSummaryQuery.isError
            }
            attendanceSummary={scopedSummary}
            careQueue={careQueue}
            household={household}
          />}

          {permissions.has("INTELLIGENCE_VIEW") && permissions.has("ATTENDANCE_VIEW") && <HealthTrendCard
            intelligence={intelligence}
            attendance={attendance}
          />}

          {permissions.has("ATTENDANCE_VIEW") && <AttendanceCard
            records={attendance}
            summary={attendanceSummary}
            isLoading={
              attendanceQuery.isLoading ||
              attendanceSummaryQuery.isLoading
            }
            isError={
              attendanceQuery.isError ||
              attendanceSummaryQuery.isError
            }
          />}

          {permissions.has("CARE_VIEW") && <CareQueueCard
            careQueue={careQueue}
            isLoading={careQueueQuery.isLoading}
            isError={careQueueQuery.isError}
          />}
        </div>

        <div className="space-y-6">
          {["INTELLIGENCE_VIEW", "ATTENDANCE_VIEW", "CARE_VIEW", "HOUSEHOLDS_VIEW"].every(code => permissions.has(code)) && <RiskFactorsCard
            intelligence={intelligence}
            attendanceSummary={scopedSummary}
            careQueue={careQueue}
            household={household}
          />}

          {permissions.has("INTELLIGENCE_VIEW") && <RecommendedActionsCard
            intelligence={intelligence}
          />}

          <MemberMinistryMemberships key={member.id} churchId={churchId!} memberId={member.id} memberName={`${member.first_name} ${member.last_name}`} />

          <MemberApplicationAccessCard churchId={churchId!} memberId={member.id} defaultEmail={member.email} />

          <ProfileDetailsCard member={member} />

          {permissions.has("HOUSEHOLDS_VIEW") && <HouseholdCard
            household={household}
            isLoading={householdQuery.isLoading}
            isError={householdQuery.isError}
          />}
        </div>
      </div>
      <MemberDeletion member={member} />
    </PageContainer>
  );
}

function MemberHero({
  member,
  household,
  displayName,
}: {
  member: MemberResponse;
  household?: HouseholdSummary | null;
  displayName: string;
}) {
  const { permissions } = useEffectivePermissions();
  const initials =
    `${member.first_name[0] ?? ""}${member.last_name[0] ?? ""}`;

  return (
    <Card className="relative overflow-hidden p-7">
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-500 via-violet-500 to-emerald-500" />

      <div className="flex flex-col gap-6 lg:flex-row lg:items-center">
        <div className="flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-500 to-violet-600 text-4xl font-bold text-white shadow-lg shadow-indigo-950/30">
          {member.photo_url ? (
            <img
              src={member.photo_url}
              alt={displayName}
              className="h-full w-full object-cover"
            />
          ) : (
            initials
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-bold tracking-tight text-white">
              {displayName}
            </h1>

            <StatusBadge status={member.status} />

            <span className="rounded-full border border-indigo-500/20 bg-indigo-500/10 px-3 py-1 text-xs font-semibold text-indigo-300">
              {member.member_type}
            </span>
          </div>

          {member.preferred_name &&
            displayName !== fullMemberName(member) && (
              <p className="mt-2 text-sm text-slate-500">
                Full name: {fullMemberName(member)}
              </p>
            )}

          <div className="mt-5 grid gap-3 text-sm text-slate-300 sm:grid-cols-2 xl:grid-cols-4">
            <HeroDetail
              icon={<Mail size={17} />}
              label={
                member.email ||
                "No email on file"
              }
            />

            <HeroDetail
              icon={<Phone size={17} />}
              label={
                member.phone ||
                "No phone on file"
              }
            />

            {permissions.has("HOUSEHOLDS_VIEW") && <HeroDetail
              icon={<Home size={17} />}
              label={
                household?.household_name ||
                "No household assigned"
              }
            />}

            {permissions.has("ATTENDANCE_VIEW") && <HeroDetail
              icon={<CalendarDays size={17} />}
              label={
                member.last_attended_at
                  ? `Last attended ${formatDate(
                      member.last_attended_at
                    )}`
                  : "No attendance history"
              }
            />}
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-start gap-3 lg:items-end">
          <EditMemberDialog member={member} />

          <p className="text-sm text-slate-500">
            Member since{" "}
            <span className="font-medium text-slate-300">
              {member.date_joined
                ? formatDate(member.date_joined)
                : "Unknown"}
            </span>
          </p>
        </div>
      </div>
    </Card>
  );
}

function HeroDetail({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className="shrink-0 text-slate-500">
        {icon}
      </span>

      <span className="truncate">
        {label}
      </span>
    </div>
  );
}

function IntelligenceOverview({
  intelligence,
  isLoading,
  isError,
  attendanceSummary,
  careQueue,
  household,
}: {
  intelligence?: MemberIntelligenceResponse;
  isLoading: boolean;
  isError: boolean;
  attendanceSummary?: MemberAttendanceSummary;
  careQueue?: CareQueueListResponse;
  household?: HouseholdSummary | null;
}) {
  if (isLoading) {
    return (
      <LoadingCard title="MinistryIQ Intelligence" />
    );
  }

  if (isError) return <ErrorCard title="MinistryIQ Intelligence" />;
  if (!intelligence) return <Card className="p-6 text-slate-400">No current assessment is available for this context.</Card>;

  const healthScore = riskToHealth(
    intelligence.score
  );

  const visual = getLevelVisual(
    intelligence.level
  );

  const summary = attendanceSummary ? buildMemberSummary(
    intelligence,
    attendanceSummary,
    careQueue,
    household
  ) : intelligence.reasons.join(" ") || "Current assessment for the selected attendance context.";

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-col gap-6 border-b border-slate-800 p-6 lg:flex-row lg:items-center">
        <div
          className={`relative flex h-40 w-40 shrink-0 items-center justify-center rounded-full border-[10px] ${visual.ringClass}`}
        >
          <div className="text-center">
            <p
              className={`text-5xl font-bold ${visual.textClass}`}
            >
              {healthScore}
            </p>

            <p className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
              Health
            </p>
          </div>
        </div>

        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <div
              className={`rounded-xl p-3 ${visual.iconClass}`}
            >
              <Brain size={24} />
            </div>

            <div>
              <p className="text-sm font-medium text-slate-400">
                MinistryIQ Intelligence
              </p>

              <h2 className="text-2xl font-bold text-white">
                Scoped Member Health Overview
              </h2>
            </div>

            <span
              className={`ml-auto rounded-full px-3 py-1 text-xs font-semibold ${visual.badgeClass}`}
            >
              {formatLevel(
                intelligence.level
              )}
            </span>
          </div>

          <p className="mt-5 max-w-3xl text-base leading-7 text-slate-300">
            {summary}
          </p>

          <div className="mt-5 flex flex-wrap gap-3">
            <MetricPill
              label="AI Confidence"
              value={`${Math.round(
                intelligence.confidence * 100
              )}%`}
            />

            <MetricPill
              label="Risk Score"
              value={`${intelligence.score}/100`}
            />

            <MetricPill
              label="Engine"
              value={`v${intelligence.engine_version}`}
            />
          </div>
        </div>
      </div>

      <div className="grid gap-6 p-6 lg:grid-cols-2">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles
              size={18}
              className="text-violet-400"
            />

            <h3 className="font-semibold text-white">
              What MinistryIQ Sees
            </h3>
          </div>

          <div className="mt-4 space-y-3">
            {intelligence.reasons.length === 0 ? (
              <p className="text-sm text-slate-400">
                No significant risk factors were detected.
              </p>
            ) : (
              intelligence.reasons.map((reason) => (
                <div
                  key={reason}
                  className="flex gap-3 rounded-xl border border-slate-800 bg-slate-950/50 p-4"
                >
                  <Activity
                    size={18}
                    className="mt-0.5 shrink-0 text-indigo-400"
                  />

                  <p className="text-sm leading-6 text-slate-300">
                    {reason}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck
              size={18}
              className="text-emerald-400"
            />

            <h3 className="font-semibold text-white">
              Recommended Next Step
            </h3>
          </div>

          <div className="mt-4 rounded-2xl border border-indigo-500/20 bg-indigo-500/5 p-5">
            {intelligence.recommendations[0] ? (
              <>
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold text-white">
                    {
                      intelligence
                        .recommendations[0]
                        .title
                    }
                  </p>

                  <span className="rounded-full bg-indigo-500/15 px-3 py-1 text-xs font-semibold text-indigo-300">
                    {
                      intelligence
                        .recommendations[0]
                        .priority
                    }
                  </span>
                </div>

                <p className="mt-3 leading-6 text-slate-300">
                  {
                    intelligence
                      .recommendations[0]
                      .action
                  }
                </p>

                <p className="mt-3 text-sm text-slate-500">
                  {
                    intelligence
                      .recommendations[0]
                      .reason
                  }
                </p>
              </>
            ) : (
              <>
                <p className="font-semibold text-white">
                  Continue normal pastoral awareness
                </p>

                <p className="mt-3 text-sm leading-6 text-slate-400">
                  No immediate intervention is recommended
                  based on the information currently
                  available.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function RiskFactorsCard({
  intelligence,
  attendanceSummary,
  careQueue,
  household,
}: {
  intelligence?: MemberIntelligenceResponse;
  attendanceSummary?: MemberAttendanceSummary;
  careQueue?: CareQueueListResponse;
  household?: HouseholdSummary | null;
}) {
  const attendanceRate =
    attendanceSummary?.attendance_rate ?? null;

  const openCare =
    careQueue?.items.filter((item) =>
      ["OPEN", "IN_PROGRESS"].includes(
        item.status
      )
    ).length ?? 0;

  const factors = [
    {
      label: "Attendance engagement",
      healthy: attendanceRate === null ? null : attendanceRate >= 75,
      detail:
        !attendanceSummary
          ? "Attendance metrics unavailable for this assessment"
          : attendanceRate === null
          ? "No eligible service history"
          : `${attendanceRate}% attendance - ${
              attendanceSummary?.missed_services ??
              0
            } missed - ${
              attendanceSummary
                ?.consecutive_absences ?? 0
            } consecutive`,
    },
    {
      label: "Care follow-up",
      healthy: openCare === 0,
      detail:
        openCare === 0
          ? "No open care items"
          : `${openCare} open care item${
              openCare === 1 ? "" : "s"
            }`,
    },
    {
      label: "Household support",
      healthy:
        !!household &&
        household.health_score >= 70,
      detail: household
        ? `${household.household_name} health ${household.health_score}`
        : "No household assigned",
    },
    {
      label: "Context intelligence",
      healthy: intelligence ? intelligence.score < 25 : null,
      detail: intelligence
        ? formatLevel(intelligence.level)
        : "Awaiting assessment",
    },
  ];

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3">
        <div className="rounded-xl bg-amber-500/15 p-3 text-amber-400">
          <AlertTriangle size={21} />
        </div>

        <div>
          <h2 className="text-xl font-semibold text-white">
            Risk Factors
          </h2>

          <p className="text-sm text-slate-500">
            Signals influencing member health
          </p>
        </div>
      </div>

      <div className="mt-5 space-y-3">
        {factors.map((factor) => (
          <div
            key={factor.label}
            className="flex items-center justify-between gap-4 rounded-xl border border-slate-800 bg-slate-950/50 p-4"
          >
            <div className="flex items-center gap-3">
              {factor.healthy === null ? <span className="text-slate-400">—</span> : factor.healthy ? (
                <CheckCircle2
                  size={18}
                  className="text-emerald-400"
                />
              ) : (
                <AlertTriangle
                  size={18}
                  className="text-amber-400"
                />
              )}

              <div>
                <p className="font-medium text-white">
                  {factor.label}
                </p>

                <p className="mt-1 text-xs text-slate-500">
                  {factor.detail}
                </p>
              </div>
            </div>

            <span
              className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                factor.healthy === null
                  ? "bg-slate-800 text-slate-300"
                  : factor.healthy
                  ? "bg-emerald-500/15 text-emerald-300"
                  : "bg-amber-500/15 text-amber-300"
              }`}
            >
              {factor.healthy === null
                ? "Not assessed"
                : factor.healthy
                ? "Healthy"
                : "Review"}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function RecommendedActionsCard({
  intelligence,
}: {
  intelligence?: MemberIntelligenceResponse;
}) {
  if (!intelligence) {
    return <Card className="p-6 text-slate-400">Suggested actions are awaiting assessment.</Card>;
  }
  const recommendations = intelligence.recommendations;

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3">
        <div className="rounded-xl bg-indigo-500/15 p-3 text-indigo-400">
          <Sparkles size={21} />
        </div>

        <div>
          <h2 className="text-xl font-semibold text-white">
            Suggested Actions
          </h2>

          <p className="text-sm text-slate-500">
            Practical pastoral next steps
          </p>
        </div>
      </div>

      <div className="mt-5 space-y-3">
        {recommendations.length === 0 ? (
          <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
            <p className="font-medium text-emerald-300">
              No immediate action required
            </p>

            <p className="mt-2 text-sm text-slate-400">
              Continue normal pastoral awareness.
            </p>
          </div>
        ) : (
          recommendations.map(
            (recommendation) => (
              <div
                key={`${recommendation.title}-${recommendation.action}`}
                className="rounded-xl border border-slate-800 bg-slate-950/50 p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-white">
                      {recommendation.title}
                    </p>

                    <p className="mt-2 text-sm leading-6 text-slate-300">
                      {recommendation.action}
                    </p>
                  </div>

                  <span className="rounded-full bg-indigo-500/15 px-2.5 py-1 text-xs font-semibold text-indigo-300">
                    {recommendation.priority}
                  </span>
                </div>

                <p className="mt-3 border-t border-slate-800 pt-3 text-xs leading-5 text-slate-500">
                  {recommendation.reason}
                </p>
              </div>
            )
          )
        )}
      </div>
    </Card>
  );
}

function HealthTrendCard({
  intelligence,
  attendance,
}: {
  intelligence?: MemberIntelligenceResponse;
  attendance: MemberAttendanceRecord[];
}) {
  if (!intelligence) return <Card className="p-6 text-slate-400">Member intelligence is not yet available.</Card>;
  const currentHealth = riskToHealth(intelligence.score);

  const chronological = [...attendance]
    .sort(
      (a, b) =>
        new Date(a.service_date).getTime() -
        new Date(b.service_date).getTime()
    )
    .slice(-12);

  const initialHealth = Math.min(100, currentHealth + chronological.length * 3);
  const chartData = chronological.reduce<{ date: string; health: number }[]>((rows, record, index) => {
    const previous = rows.at(-1)?.health ?? initialHealth;
    const health = Math.max(0, Math.min(100, previous + (record.status === "PRESENT" ? 2 : -6)));
    return [...rows, {
      date: new Date(record.service_date).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      health: index === chronological.length - 1 ? currentHealth : health,
    }];
  }, []);

  return (
    <Card className="p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">
            Health Trend
          </h2>

          <p className="mt-1 text-sm text-slate-500">
            Current health with recent attendance
            context
          </p>
        </div>

        <div className="text-right">
          <p className="text-3xl font-bold text-white">
            {currentHealth}
          </p>

          <p className="text-xs text-slate-500">
            Current health
          </p>
        </div>
      </div>

      {chartData.length < 2 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-slate-700 bg-slate-950/40 p-8 text-center">
          <p className="font-medium text-white">
            More history is needed
          </p>

          <p className="mt-2 text-sm text-slate-400">
            The health trend will become more
            meaningful as attendance history
            accumulates.
          </p>
        </div>
      ) : (
        <div className="mt-6 h-64">
          <ResponsiveContainer
            width="100%"
            height="100%"
          >
            <AreaChart data={chartData}>
              <defs>
                <linearGradient
                  id="memberHealth"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor="currentColor"
                    stopOpacity={0.35}
                  />

                  <stop
                    offset="95%"
                    stopColor="currentColor"
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>

              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#1e293b"
              />

              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{
                  fill: "#64748b",
                  fontSize: 12,
                }}
              />

              <YAxis
                domain={[0, 100]}
                axisLine={false}
                tickLine={false}
                tick={{
                  fill: "#64748b",
                  fontSize: 12,
                }}
              />

              <Tooltip
                contentStyle={{
                  background: "#0f172a",
                  border: "1px solid #334155",
                  borderRadius: "12px",
                }}
              />

              <Area
                type="monotone"
                dataKey="health"
                stroke="#818cf8"
                strokeWidth={3}
                fill="url(#memberHealth)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

function AttendanceCard({
  records,
  summary,
  isLoading,
  isError,
}: {
  records: MemberAttendanceRecord[];
  summary?: MemberAttendanceSummary;
  isLoading: boolean;
  isError: boolean;
}) {
  if (isLoading) {
    return (
      <LoadingCard title="Attendance" />
    );
  }

  if (isError) {
    return (
      <ErrorCard title="Attendance" />
    );
  }

  const attendanceRate =
    summary?.attendance_rate;

  const visitorHistoryCount =
    records.filter(
      (record) =>
        record.attendance_source === "VISITOR"
    ).length;

  return (
    <Card className="p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-emerald-500/15 p-3 text-emerald-400">
            <CalendarDays size={22} />
          </div>

          <div>
            <h2 className="text-xl font-semibold text-white">
              Attendance
            </h2>

            <p className="text-sm text-slate-500">
              Last{" "}
              {summary?.eligible_services ?? 0}{" "}
              eligible services across Main Church and ministries
            </p>
          </div>
        </div>

        <div className="text-left sm:text-right">
          <p
            className={`text-3xl font-bold ${
              attendanceRate === null ||
              attendanceRate === undefined
                ? "text-slate-400"
                : attendanceRate >= 75
                  ? "text-emerald-400"
                  : attendanceRate >= 50
                    ? "text-amber-400"
                    : "text-rose-400"
            }`}
          >
            {attendanceRate === null ||
            attendanceRate === undefined
              ? "-"
              : `${attendanceRate}%`}
          </p>

          <p className="text-xs text-slate-500">
            Attendance rate
          </p>
        </div>
      </div>

      {!summary ||
      summary.eligible_services === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-slate-700 bg-slate-950/40 p-8 text-center">
          <p className="font-medium text-white">
            No eligible service history
          </p>

          <p className="mt-2 text-sm text-slate-400">
            Attendance metrics will appear after
            past service occurrences are available.
          </p>
        </div>
      ) : (
        <>
          <div className="mt-7 grid gap-4 sm:grid-cols-4">
            <AttendanceStat
              label="Attended"
              value={summary.attended_services}
              className="text-emerald-400"
            />

            <AttendanceStat
              label="Missed"
              value={summary.missed_services}
              className="text-rose-400"
            />

            <AttendanceStat
              label="Consecutive Misses"
              value={
                summary.consecutive_absences
              }
              className="text-amber-400"
            />

            <AttendanceStat
              label="Visitor Visits"
              value={visitorHistoryCount}
              className="text-sky-400"
            />
          </div>

          {records.length === 0 ? (
            <div className="mt-7 rounded-2xl border border-dashed border-slate-700 bg-slate-950/40 p-8 text-center">
              <p className="font-medium text-white">
                No attended services recorded
              </p>

              <p className="mt-2 text-sm text-slate-400">
                Eligible services exist, but this
                person has no recorded attendance in
                the attendance history.
              </p>
            </div>
          ) : null}
        </>
      )}
      <MemberAttendanceCalendar records={records} />
    </Card>
  );
}

export function MemberAttendanceCalendar({
  records,
}: {
  records: MemberAttendanceRecord[];
}) {
  const latestAttendanceDate = records.reduce<Date | null>(
    (latest, record) => {
      const current = parseCalendarDate(record.service_date);

      if (!current) {
        return latest;
      }

      if (!latest || current > latest) {
        return current;
      }

      return latest;
    },
    null
  );

  const initialDate = latestAttendanceDate ?? new Date();

  const [visibleMonth, setVisibleMonth] = useState(
    new Date(initialDate.getFullYear(), initialDate.getMonth(), 1)
  );
  const [selectedDateKey, setSelectedDateKey] = useState<string | null>(
    latestAttendanceDate ? toDateKey(latestAttendanceDate) : null
  );

  const { churchId } = useAuth();
  const { memberId } = useParams();
  const monthKey = `${visibleMonth.getFullYear()}-${String(visibleMonth.getMonth() + 1).padStart(2, "0")}-01`;
  const monthQuery = useQuery({
    queryKey: ["member-attendance-calendar", churchId, memberId, monthKey],
    enabled: !!churchId && !!memberId,
    queryFn: () => getMemberAttendanceHistory(churchId!, memberId!, monthKey),
  });
  const recordsByDate = (monthQuery.data ?? []).reduce<
    Record<string, MemberAttendanceRecord[]>
  >((grouped, record) => {
    const date = parseCalendarDate(record.service_date);

    if (!date) {
      return grouped;
    }

    const key = toDateKey(date);
    grouped[key] = [...(grouped[key] ?? []), record];
    return grouped;
  }, {});

  const firstDay = new Date(
    visibleMonth.getFullYear(),
    visibleMonth.getMonth(),
    1
  );
  const lastDay = new Date(
    visibleMonth.getFullYear(),
    visibleMonth.getMonth() + 1,
    0
  );

  const days: (Date | null)[] = [
    ...Array(firstDay.getDay()).fill(null),
    ...Array.from(
      { length: lastDay.getDate() },
      (_, index) =>
        new Date(
          visibleMonth.getFullYear(),
          visibleMonth.getMonth(),
          index + 1
        )
    ),
  ];

  while (days.length % 7 !== 0) {
    days.push(null);
  }

  const selectedRecords = selectedDateKey
    ? recordsByDate[selectedDateKey] ?? []
    : [];

  const moveMonth = (offset: number) => {
    setVisibleMonth(
      (current) =>
        new Date(
          current.getFullYear(),
          current.getMonth() + offset,
          1
        )
    );
    setSelectedDateKey(null);
  };

  return (
    <div className="mt-7 overflow-hidden rounded-2xl border border-slate-800 bg-slate-950/30">
      {monthQuery.isLoading && <p role="status" className="p-4 text-slate-400">Loading attendance for this month...</p>}
      {monthQuery.isError && <p role="alert" className="p-4 text-rose-300">Unable to load this month. <button type="button" onClick={() => void monthQuery.refetch()}>Retry</button></p>}
      <div className="flex items-center justify-between border-b border-slate-800 px-5 py-4">
        <button
          type="button"
          onClick={() => moveMonth(-1)}
          className="rounded-lg border border-slate-800 p-2 text-slate-400 transition hover:bg-slate-800 hover:text-white"
          aria-label="Previous month"
        >
          <ChevronLeft size={18} />
        </button>

        <div className="text-center">
          <p className="font-semibold text-white">
            {visibleMonth.toLocaleDateString(undefined, {
              month: "long",
              year: "numeric",
            })}
          </p>

          <p className="mt-1 text-xs text-slate-500">
            Highlighted dates have recorded attendance
          </p>
        </div>

        <button
          type="button"
          onClick={() => moveMonth(1)}
          className="rounded-lg border border-slate-800 p-2 text-slate-400 transition hover:bg-slate-800 hover:text-white"
          aria-label="Next month"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      <div className="grid grid-cols-7 border-b border-slate-800 bg-slate-950/50">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
          (day) => (
            <div
              key={day}
              className="px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500"
            >
              {day}
            </div>
          )
        )}
      </div>

      <div className="grid grid-cols-7">
        {days.map((date, index) => {
          if (!date) {
            return (
              <div
                key={`empty-${index}`}
                className="min-h-24 border-b border-r border-slate-800/70 bg-slate-950/20"
              />
            );
          }

          const key = toDateKey(date);
          const dayRecords = recordsByDate[key] ?? [];
          const attended = dayRecords.length > 0;
          const selected = selectedDateKey === key;

          return (
            <button
              key={key}
              type="button"
              onClick={() =>
                attended && setSelectedDateKey(key)
              }
              className={`relative min-h-24 border-b border-r border-slate-800/70 p-3 text-left transition ${
                attended
                  ? "cursor-pointer bg-emerald-500/5 hover:bg-emerald-500/10"
                  : "cursor-default bg-slate-950/20"
              } ${
                selected
                  ? "ring-1 ring-inset ring-emerald-400/70"
                  : ""
              }`}
              aria-label={`${formatDateKeyForDisplay(key)}${
                attended
                  ? `, ${dayRecords.length} attendance record${
                      dayRecords.length === 1 ? "" : "s"
                    }`
                  : ", no attendance recorded"
              }`}
            >
              <span
                className={`inline-flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${
                  attended
                    ? "bg-emerald-500 text-white"
                    : "text-slate-400"
                }`}
              >
                {date.getDate()}
              </span>

              {attended && (
                <div className="mt-2 space-y-1">
                  {dayRecords.slice(0, 2).map((record) => (
                    <p
                      key={record.id}
                      className="truncate text-xs font-medium text-emerald-300"
                    >
                      {record.ministry_name ?? "Main Church"} - {record.service_type}
                    </p>
                  ))}

                  {dayRecords.length > 2 && (
                    <p className="text-xs text-slate-500">
                      +{dayRecords.length - 2} more
                    </p>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className="border-t border-slate-800 p-5">
        {monthQuery.isLoading || monthQuery.isError ? null : !selectedDateKey ? (
          <p className="text-sm text-slate-500">
            Select a highlighted date to view service details.
          </p>
        ) : selectedRecords.length === 0 ? (
          <p className="text-sm text-slate-500">
            No attendance recorded for{" "}
            {formatDateKeyForDisplay(selectedDateKey)}.
          </p>
        ) : (
          <div>
            <p className="text-sm font-semibold text-white">
              {formatDateKeyForDisplay(selectedDateKey)}
            </p>

            <div className="mt-3 space-y-3">
              {selectedRecords.map((record) => (
                <div
                  key={record.id}
                  className="flex flex-col gap-2 rounded-xl border border-slate-800 bg-slate-950/50 p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <p className="font-medium text-white">
                      {record.service_type}
                    </p>
                    <p className="text-sm text-slate-400">{record.ministry_name ?? "Main Church"}{record.ministry_archived ? " (Archived)" : ""}</p>

                    <p className="mt-1 text-xs text-slate-500">
                      {record.attendance_source === "VISITOR"
                        ? "Recorded as visitor attendance"
                        : "Member attendance"}
                    </p>
                  </div>

                  <span
                    className={`w-fit rounded-full px-3 py-1.5 text-xs font-semibold ${
                      record.attendance_source === "VISITOR"
                        ? "bg-sky-500/15 text-sky-300"
                        : "bg-emerald-500/15 text-emerald-300"
                    }`}
                  >
                    {record.attendance_source === "VISITOR"
                      ? "Attended as Visitor"
                      : "Present"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function parseCalendarDate(value: string): Date | null {
  const dateOnly = value.slice(0, 10);
  const parts = dateOnly.split("-").map(Number);

  if (
    parts.length === 3 &&
    parts.every((part) => Number.isFinite(part))
  ) {
    const [year, month, day] = parts;
    return new Date(year, month - 1, day);
  }

  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

function toDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function formatDateKeyForDisplay(value: string): string {
  const date = parseCalendarDate(value);

  if (!date) {
    return value;
  }

  return date.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function CareQueueCard({
  careQueue,
  isLoading,
  isError,
}: {
  careQueue?: CareQueueListResponse;
  isLoading: boolean;
  isError: boolean;
}) {
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-rose-500/15 p-3 text-rose-400">
            <HeartHandshake size={21} />
          </div>

          <div>
            <h2 className="text-xl font-semibold text-white">
              Care/Follow-up
            </h2>

            <p className="text-sm text-slate-500">
              Member care and follow-up
            </p>
          </div>
        </div>

        <Link
          to="/care-queue"
          className="text-sm font-medium text-indigo-400 hover:text-indigo-300"
        >
          View all
        </Link>
      </div>

      {isLoading && (
        <p className="mt-5 text-sm text-slate-400">
          Loading care items...
        </p>
      )}

      {isError && (
        <p className="mt-5 text-sm text-rose-400">
          Unable to load care items.
        </p>
      )}

      {!isLoading &&
        !isError &&
        (careQueue?.items.length ?? 0) === 0 && (
          <div className="mt-5 rounded-2xl border border-dashed border-slate-700 bg-slate-950/40 p-7 text-center">
            <CheckCircle2 className="mx-auto text-emerald-400" />

            <p className="mt-3 font-medium text-white">
              No open care items
            </p>

            <p className="mt-2 text-sm text-slate-400">
              This member currently has no recorded
              care follow-up.
            </p>
          </div>
        )}

      <div className="mt-5 space-y-3">
        {careQueue?.items
          .slice(0, 4)
          .map((item) => (
            <div
              key={item.id}
              className="rounded-xl border border-slate-800 bg-slate-950/50 p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-white">
                    {item.title}
                  </p>

                  <p className="mt-2 text-sm leading-6 text-slate-400">
                    {item.description ||
                      item.recommended_action}
                  </p>
                </div>

                <span className="rounded-full bg-rose-500/15 px-2.5 py-1 text-xs font-semibold text-rose-300">
                  {item.priority}
                </span>
              </div>

              <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                <span>{item.status}</span>

                <span>
                  {formatDate(item.created_at)}
                </span>
              </div>
              <CareHistory item={item} />
            </div>
          ))}
      </div>
    </Card>
  );
}

function ProfileDetailsCard({
  member,
}: {
  member: MemberResponse;
}) {
  const { permissions } = useEffectivePermissions();
  return (
    <Card className="p-6">
      <div className="flex items-center gap-3">
        <div className="rounded-xl bg-blue-500/15 p-3 text-blue-400">
          <UserRound size={21} />
        </div>

        <div>
          <h2 className="text-xl font-semibold text-white">
            Profile Details
          </h2>

          <p className="text-sm text-slate-500">
            Administrative member information
          </p>
        </div>
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <Detail
          label="Status"
          value={member.status}
        />

        <Detail
          label="Member Type"
          value={member.member_type}
        />

        <Detail
          label="Date Joined"
          value={
            member.date_joined
              ? formatDate(member.date_joined)
              : "Not recorded"
          }
        />

        <Detail
          label="Birthday"
          value={formatPrivateDate(
            member.birth_date,
            member.hide_birth_year
          )}
        />

        <Detail
          label="Anniversary"
          value={formatPrivateDate(
            member.anniversary_date,
            member.hide_anniversary_year
          )}
        />

        {permissions.has("HOUSEHOLDS_VIEW") && <Detail
          label="Household Role"
          value={
            member.relationship_to_household ||
            "Not assigned"
          }
        />}
      </div>
    </Card>
  );
}

function HouseholdCard({
  household,
  isLoading,
  isError,
}: {
  household?: HouseholdSummary | null;
  isLoading: boolean;
  isError: boolean;
}) {
  const { permissions } = useEffectivePermissions();
  if (isLoading) {
    return (
      <LoadingCard title="Household" />
    );
  }

  if (isError) {
    return (
      <ErrorCard title="Household" />
    );
  }

  if (!household) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold text-white">
          Household
        </h2>

        <div className="mt-5 rounded-2xl border border-dashed border-slate-700 bg-slate-950/40 p-7 text-center">
          <Home className="mx-auto text-slate-500" />

          <p className="mt-3 font-medium text-white">
            No household assigned
          </p>

          <p className="mt-2 text-sm text-slate-400">
            Assign this member to a household from
            Edit Member.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <div className="border-b border-slate-800 p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-white">
              {household.household_name}
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              {household.member_count} household
              member
              {household.member_count === 1
                ? ""
                : "s"}
            </p>
          </div>

          <Link
            to={`/households/${household.id}`}
            className="flex items-center gap-1 text-sm font-medium text-indigo-400 hover:text-indigo-300"
          >
            View
            <ArrowRight size={15} />
          </Link>
        </div>

        <div className="mt-5 grid grid-cols-3 gap-3">
          {permissions.has("INTELLIGENCE_VIEW") && <MiniMetric
            label="Health"
            value={household.health_score ?? "Unavailable"}
          />}

          {permissions.has("ATTENDANCE_VIEW") && <MiniMetric
            label="Attendance"
            value={household.attendance_rate === null ? "Unavailable" : `${household.attendance_rate}%`}
          />}

          {permissions.has("CARE_VIEW") && <MiniMetric
            label="Open Care"
            value={household.open_care_items ?? "Unavailable"}
          />}
        </div>
      </div>

      <div className="divide-y divide-slate-800">
        {household.members.map(
          (householdMember) => (
            <Link
              key={householdMember.id}
              to={`/members/${householdMember.id}`}
              className="flex items-center justify-between gap-3 px-6 py-4 transition hover:bg-slate-800/40"
            >
              <div>
                <p className="font-medium text-white">
                  {householdMember.first_name}{" "}
                  {householdMember.last_name}
                </p>

                <p className="mt-1 text-xs text-slate-500">
                  {householdMember.relationship_to_household ||
                    "Household member"}
                </p>
              </div>

              {householdMember.is_head_of_household && (
                <span className="rounded-full bg-amber-500/15 px-2.5 py-1 text-xs font-semibold text-amber-300">
                  Head
                </span>
              )}
            </Link>
          )
        )}
      </div>
    </Card>
  );
}

function AttendanceStat({
  label,
  value,
  className,
}: {
  label: string;
  value: number;
  className: string;
}) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4">
      <p
        className={`text-2xl font-bold ${className}`}
      >
        {value}
      </p>

      <p className="mt-1 text-xs text-slate-500">
        {label}
      </p>
    </div>
  );
}

function MetricPill({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-full border border-slate-800 bg-slate-950/50 px-3 py-2 text-xs">
      <span className="text-slate-500">
        {label}
      </span>

      <span className="ml-2 font-semibold text-white">
        {value}
      </span>
    </div>
  );
}

function Detail({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4">
      <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
        {label}
      </p>

      <p className="mt-2 font-medium text-white">
        {value}
      </p>
    </div>
  );
}

function MiniMetric({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-3">
      <p className="text-xs text-slate-500">
        {label}
      </p>

      <p className="mt-1 text-lg font-bold text-white">
        {value}
      </p>
    </div>
  );
}

function LoadingCard({
  title,
}: {
  title: string;
}) {
  return (
    <Card className="p-6">
      <h2 className="text-xl font-semibold text-white">
        {title}
      </h2>

      <div className="mt-5 animate-pulse space-y-3">
        <div className="h-4 rounded bg-slate-800" />
        <div className="h-4 w-4/5 rounded bg-slate-800" />
        <div className="h-4 w-3/5 rounded bg-slate-800" />
      </div>
    </Card>
  );
}

function ErrorCard({
  title,
}: {
  title: string;
}) {
  return (
    <Card className="border-rose-500/20 p-6">
      <h2 className="text-xl font-semibold text-white">
        {title}
      </h2>

      <p className="mt-4 text-sm text-rose-400">
        Unable to load this information.
      </p>
    </Card>
  );
}

function getLevelVisual(
  level: MemberIntelligenceResponse["level"]
) {
  const visuals = {
    HEALTHY: {
      textClass: "text-emerald-400",
      ringClass: "border-emerald-500/80",
      badgeClass:
        "bg-emerald-500/15 text-emerald-300",
      iconClass:
        "bg-emerald-500/15 text-emerald-400",
    },
    WATCH: {
      textClass: "text-amber-400",
      ringClass: "border-amber-500/80",
      badgeClass:
        "bg-amber-500/15 text-amber-300",
      iconClass:
        "bg-amber-500/15 text-amber-400",
    },
    AT_RISK: {
      textClass: "text-orange-400",
      ringClass: "border-orange-500/80",
      badgeClass:
        "bg-orange-500/15 text-orange-300",
      iconClass:
        "bg-orange-500/15 text-orange-400",
    },
    CRITICAL: {
      textClass: "text-rose-400",
      ringClass: "border-rose-500/80",
      badgeClass:
        "bg-rose-500/15 text-rose-300",
      iconClass:
        "bg-rose-500/15 text-rose-400",
    },
  };

  return visuals[level];
}

function buildMemberSummary(
  intelligence: MemberIntelligenceResponse,
  attendanceSummary?: MemberAttendanceSummary,
  careQueue?: CareQueueListResponse,
  household?: HouseholdSummary | null
) {
  const attendanceRate =
    attendanceSummary?.attendance_rate ?? null;

  const openCare =
    careQueue?.items.filter((item) =>
      ["OPEN", "IN_PROGRESS"].includes(
        item.status
      )
    ).length ?? 0;

  const parts: string[] = [];

  if (
    attendanceRate === null ||
    !attendanceSummary
  ) {
    parts.push(
      "There are no eligible completed services available to establish an attendance pattern."
    );
  } else if (attendanceRate >= 80) {
    parts.push(
      `Attendance is strong at ${attendanceRate}% across ${attendanceSummary.eligible_services} eligible services.`
    );
  } else if (attendanceRate >= 60) {
    parts.push(
      `Attendance is currently ${attendanceRate}% across ${attendanceSummary.eligible_services} eligible services and should be watched.`
    );
  } else {
    parts.push(
      `Attendance is low at ${attendanceRate}% with ${attendanceSummary.missed_services} missed service${
        attendanceSummary.missed_services === 1
          ? ""
          : "s"
      } and may require follow-up.`
    );
  }

  if (
    attendanceSummary &&
    attendanceSummary.consecutive_absences > 0
  ) {
    parts.push(
      `${attendanceSummary.consecutive_absences} consecutive service${
        attendanceSummary.consecutive_absences === 1
          ? " has"
          : "s have"
      } been missed.`
    );
  }

  if (openCare > 0) {
    parts.push(
      `${openCare} open care item${
        openCare === 1
          ? " requires"
          : "s require"
      } attention.`
    );
  } else {
    parts.push(
      "No open care items are currently recorded."
    );
  }

  if (household) {
    parts.push(
      `${household.household_name} family currently has a household health score of ${household.health_score}.`
    );
  }

  if (intelligence.level === "HEALTHY") {
    parts.push(
      "The member currently appears healthy."
    );
  } else {
    parts.push(
      `MinistryIQ currently classifies this member as ${formatLevel(
        intelligence.level
      ).toLowerCase()}.`
    );
  }

  return parts.join(" ");
}

function riskToHealth(
  riskScore: number
) {
  return Math.max(
    0,
    Math.min(100, 100 - riskScore)
  );
}

function formatLevel(
  level: string
) {
  return level.replaceAll("_", " ");
}

function formatDate(
  value: string
) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString(
    undefined,
    {
      month: "short",
      day: "numeric",
      year: "numeric",
    }
  );
}

function formatPrivateDate(
  value?: string | null,
  hideYear = false
) {
  if (!value) {
    return "Not recorded";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString(
    undefined,
    {
      month: "long",
      day: "numeric",
      year: hideYear
        ? undefined
        : "numeric",
    }
  );
}

function fullMemberName(
  member: MemberResponse
) {
  return [
    member.first_name,
    member.middle_name,
    member.last_name,
  ]
    .filter(Boolean)
    .join(" ");
}

